import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { parseApiErrors } from "../../../utils/parseApiErrors";
import { setSaveState } from "../../../reducers/settings";

const SAVED_VISIBLE_MS = 1800;

const failedSaves = new Map();
let queue = Promise.resolve();
let pendingSaves = 0;
let savedTimer = null;

export const clearFailedSaves = () => {
    clearTimeout(savedTimer);
    failedSaves.clear();
};

const useSettingsSave = () => {
    const dispatch = useDispatch();
    const { saveStatus, saveError } = useSelector((state) => state.settings);

    const save = useCallback((key, action) => {
        pendingSaves += 1;
        clearTimeout(savedTimer);
        dispatch(setSaveState({ saveStatus: "saving" }));
        queue = queue.then(async () => {
            try {
                await action();
                failedSaves.delete(key);
                return true;
            } catch (error) {
                failedSaves.set(key, { action, message: parseApiErrors(error) });
                return false;
            } finally {
                pendingSaves -= 1;
                if (!pendingSaves) {
                    const failure = failedSaves.values().next().value;
                    dispatch(setSaveState(failure ? { saveStatus: "error", saveError: failure.message } : { saveStatus: "saved" }));
                    if (!failure) savedTimer = setTimeout(() => dispatch(setSaveState({})), SAVED_VISIBLE_MS);
                }
            }
        });
        return queue;
    }, [dispatch]);

    const retry = useCallback(() => {
        failedSaves.forEach(({ action }, key) => save(key, action));
    }, [save]);

    return { save, retry, status: saveStatus, error: saveError };
};

export default useSettingsSave;
